import { useCallback } from 'react';
import { RealtimeEventHandlerMapping } from './types';
import RealtimeListener from './RealtimeListener';

/**
 * Subscribe to realtime events.
 * This hook is used to subscribe to realtime events
 * from the server.
 * The hook returns a subscribe function that can be used to subscribe
 * to a channel and an unsubscribe function that can be used to unsubscribe from a channel.
 *
 * @example
 * const { subscribe, unsubscribe } = useRealtimeSubscription();
 *
 * useEffect(() => {
 *  subscribe('channel-name', 'event-name', console.log);
 *
 *  return unsubscribe('channel-name');
 * }, [subscribe, unsubscribe]);
 *
 */
export const useRealtimeSubscription = (pushkinClient: typeof RealtimeListener) => {
    const subscribe = useCallback(
        async (channelName: string, handlerMapping: RealtimeEventHandlerMapping) => {
            await pushkinClient.subscribe(channelName, handlerMapping);
        },
        [pushkinClient]
    );

    const unsubscribe = useCallback(
        (channelName: string) => {
            if (pushkinClient.unsubscribe) {
                pushkinClient.unsubscribe(channelName);
            }
        },
        [pushkinClient]
    );

    return {
        subscribe,
        unsubscribe,
    };
};
